import { InfoCircleBigIcon } from '@app/components/icons/info-circle-big';
import { Tooltip } from '@app/components/tooltip';
import { ADMIN_TOOLTIP_LOCATIONS } from '@app/features/analytics/analytics-tracking';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';

import { LicenseConsumptionData } from '../hooks/use-admin-kpis';
import { KpiErrorState } from '../kpi-error-state';
import { OrganizerSeatIcon } from './organizer-seat-icon';
import { SeatTile } from './seat-tile';

type LicenseConsumptionCardProps = {
  data: LicenseConsumptionData | null;
  isError: boolean;
};

export const LicenseConsumptionCard = ({
  data,
  isError,
}: LicenseConsumptionCardProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const seatActivityPct = data?.isUnlimited
    ? 0
    : data?.activeOrganizersPercentage ?? 0;
  const seatAllocationPct = data?.isUnlimited ? 0 : data?.usagePercentage ?? 0;

  const handleActiveUsersTooltipHover = () => {
    track('ola_admin_tab_tooltip_hover', {
      location: ADMIN_TOOLTIP_LOCATIONS.activeUsers,
    });
  };

  return (
    <div
      className="flex flex-1 items-stretch gap-3 rounded-2xl border border-neutral-b4 bg-white px-6 py-3.5 h-36 justify-around"
      data-testid="license-consumption-card"
    >
      {isError || !data ? (
        <KpiErrorState />
      ) : (
        <>
          <div className="flex shrink-0 flex-col items-center justify-center">
            <div className="flex items-center gap-2">
              <span className="text-kpi-percentage text-gray-900">
                {data.activeOrganizers}
              </span>
              <div className="h-3 w-px bg-neutral-b4" aria-hidden />
              <span className="text-title text-neutral-b2">
                {seatActivityPct}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <OrganizerSeatIcon />
              <div className="flex items-start gap-1 pt-1">
                <span className="whitespace-nowrap text-title text-neutral-b2">
                  {t('analytics.admin.organizer-seat-usage')}
                </span>
                <Tooltip
                  tooltip={t('analytics.admin.organizer-seat-usage-tooltip')}
                  placement="bottom-center"
                >
                  <span
                    className="inline-flex cursor-pointer"
                    aria-label={t(
                      'analytics.admin.organizer-seat-usage-tooltip',
                    )}
                    data-testid="seat-tile-tooltip-trigger"
                    onMouseEnter={handleActiveUsersTooltipHover}
                  >
                    <InfoCircleBigIcon width={24} height={24} aria-hidden />
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>

          <SeatTile
            count={data.usedSeats}
            percentage={seatAllocationPct}
            labelKey="analytics.admin.used-seats"
            tooltipKey="analytics.admin.used-seats-tooltip"
            tooltipLocation={ADMIN_TOOLTIP_LOCATIONS.seatsAssigned}
          />
          <SeatTile
            count={data.totalSeats}
            labelKey="analytics.admin.total-seats"
            tooltipKey="analytics.admin.total-seats-tooltip"
            tooltipLocation={ADMIN_TOOLTIP_LOCATIONS.totalSeats}
          />
        </>
      )}
    </div>
  );
};
